/* Handmade by Rosie — icon wheel and Shopify paid, nested icon lines. */
(() => {
  'use strict';
  if (customElements.get('hbr-icon-picker')) return;
  const route = path => (window.Shopify?.routes?.root || '/') + path;
  const token = () => crypto.randomUUID();
  const integer = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= 0 ? number : fallback;
  };
  const isIcon = item => item.properties?._hbr_icon_child === '1';
  const parentOf = (item, items) => items.find(parent =>
    !isIcon(parent) && parent.key === item.parent_relationship?.parent_key &&
    parent.properties?._hbr_icon_group === item.properties?._hbr_icon_group
  );
  const readJSON = element => JSON.parse(element.querySelector('script[type="application/json"]').textContent);
  async function cartRequest(path, payload) {
    const response = await fetch(route(path), {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Your bag could not be updated.');
    return result;
  }

  // The server price belongs to the add-on variant. Properties describe the paid
  // icon ONLY on the child line; removing it leaves no icon instruction behind.
  function paidForm(form, config, choices, group) {
    if (!choices.length) return form;
    if (!config.available || !config.variantId || config.price <= 0) throw new Error('Icons are temporarily unavailable. Remove the icons to continue.');
    const parentId = String(form.get('id') || '');
    if (!/^\d+$/.test(parentId) || parentId === String(config.variantId)) throw new Error('Please choose the product options again.');
    if (form.get('selling_plan')) throw new Error('Icon add-ons are available for one-time purchases only.');
    const quantity = integer(form.get('quantity') || 1);
    if (!quantity) throw new Error('Please enter a valid quantity.');
    const result = new FormData();
    for (const [name, value] of form.entries()) {
      if (name === 'id' || name === 'quantity') result.append(`items[0][${name}]`, value);
      else if (/^properties\[.+\]$/.test(name) && name !== 'properties[Icon]' && name !== 'properties[Icons]') {
        result.append(`items[0][properties][${name.slice(11, -1)}]`, value);
      } else if (name === 'sections' || name === 'sections_url') result.append(name, value);
    }
    result.set('items[0][quantity]', String(quantity));
    result.set('items[0][properties][_hbr_icon_group]', group);
    choices.forEach((choice, index) => {
      const units = integer(choice.count);
      if (!units || !Number.isSafeInteger(units * quantity)) throw new Error('Please reduce the number of icons.');
      const prefix = `items[${index + 1}]`;
      result.append(`${prefix}[id]`, String(config.variantId));
      result.append(`${prefix}[parent_id]`, parentId);
      result.append(`${prefix}[quantity]`, String(units * quantity));
      result.append(`${prefix}[properties][Icon]`, `${choice.code} · ${choice.name}`);
      result.append(`${prefix}[properties][For]`, config.productTitle);
      result.append(`${prefix}[properties][_hbr_icon_child]`, '1');
      result.append(`${prefix}[properties][_hbr_icon_group]`, group);
      result.append(`${prefix}[properties][_hbr_icon_code]`, choice.code);
      result.append(`${prefix}[properties][_hbr_icon_units]`, String(units));
    });
    return result;
  }

  class IconPicker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.config = readJSON(this);
      this.selected ||= new Map();
      this.wheel = this.querySelector('[data-icon-wheel]');
      this.options = [...this.querySelectorAll('[data-icon-option]')];
      // Start within the catalogue so both sides of the wheel are populated.
      // Preserve a customer's position when the element is reconnected.
      this.index ??= Math.min(this.options.length - 1, integer(this.config.initialIndex, Math.floor(this.options.length / 2)));
      this.grid = this.querySelector('[data-icon-grid]');
      this.add = this.querySelector('[data-icon-add]');
      this.live = this.querySelector('[data-icon-live]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.config.currency });
      const listen = (element, event, fn, options = {}) => element.addEventListener(event, fn, { ...options, signal: this.controller.signal });
      listen(this.wheel, 'scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.paint(); });
      }, { passive: true });
      listen(this.wheel, 'pointerdown', event => { this.pointer = { x: event.clientX, y: event.clientY, scroll: this.wheel.scrollLeft }; }, { passive: true });
      listen(this.wheel, 'pointermove', event => {
        if (this.pointer && Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y) > 8) this.pointer.moved = true;
      }, { passive: true });
      listen(this.wheel, 'pointercancel', () => { if (this.pointer) this.pointer.moved = true; }, { passive: true });
      listen(this.wheel, 'keydown', event => {
        const delta = { ArrowLeft: -1, ArrowRight: 1, Home: -this.index, End: this.options.length - 1 - this.index }[event.key];
        if (delta === undefined) return;
        event.preventDefault();
        const index = Math.max(0, Math.min(this.options.length - 1, this.index + delta));
        this.options[index].focus({ preventScroll: true });
        this.go(index);
      });
      listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button) || button.disabled) return;
        if (button.matches('[data-icon-option]')) {
          if (event.detail && this.pointer && (this.pointer.moved || Math.abs(this.pointer.scroll - this.wheel.scrollLeft) > 8)) return;
          this.go(this.options.indexOf(button));
        } else if (button.matches('[data-icon-prev]')) this.go(this.index - 1);
        else if (button.matches('[data-icon-next]')) this.go(this.index + 1);
        else if (button.matches('[data-icon-all]')) {
          this.grid.hidden = !this.grid.hidden;
          button.setAttribute('aria-expanded', String(!this.grid.hidden));
          button.textContent = this.grid.hidden ? 'All icons' : 'Close grid';
        } else if (button.matches('[data-icon-grid-index]')) this.go(Number(button.dataset.iconGridIndex));
        else if (button === this.add && !this.busy && this.config.available) {
          const code = this.options[this.index].dataset.iconCode;
          this.selected.set(code, (this.selected.get(code) || 0) + 1);
          this.renderSelected();
          this.live.textContent = `${this.options[this.index].dataset.iconName} added. ${this.count} icons selected, ${this.money(this.count * this.config.price)} extra.`;
        } else if (button.matches('[data-icon-remove]') && !this.busy) {
          const code = button.dataset.iconRemove;
          const count = (this.selected.get(code) || 0) - 1;
          count > 0 ? this.selected.set(code, count) : this.selected.delete(code);
          this.renderSelected();
          const next = [...this.querySelectorAll('[data-icon-remove]')].find(el => el.dataset.iconRemove === code);
          (next || this.add).focus({ preventScroll: true });
          this.live.textContent = `One icon removed. ${this.count} icons selected.`;
        }
      });
      this.resize = new ResizeObserver(() => { this.go(this.index, false); });
      this.resize.observe(this.wheel);
      this.querySelectorAll('[data-icon-init-disabled]').forEach(button => { button.disabled = false; });
      this.add.disabled = !this.config.available;
      this.renderSelected();
      this.go(this.index, false);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.resize?.disconnect(); cancelAnimationFrame(this.frame); this.frame = 0;
    }
    get count() { return [...this.selected.values()].reduce((sum, count) => sum + count, 0); }
    get active() { return !this.closest('[hidden], [inert]'); }
    money(cents) { return this.formatter.format(cents / 100); }
    formatTotal(cents) { return this.active && this.count ? this.money(cents + this.count * this.config.price) : null; }
    setBusy(busy) {
      this.busy = busy;
      this.setAttribute('aria-busy', String(busy));
      this.add.disabled = busy || !this.config.available;
      this.querySelectorAll('[data-icon-remove]').forEach(button => { button.disabled = busy; });
    }
    prepareForm(form) {
      const choices = this.active ? [...this.selected].map(([code, count]) => {
        const option = this.options.find(option => option.dataset.iconCode === code);
        return { code, name: option.dataset.iconName, count };
      }) : [];
      const group = choices.length ? token() : '';
      const result = paidForm(form, this.config, choices, group);
      this.pending = choices.length ? { group, choices, quantity: integer(form.get('quantity') || 1) } : null;
      return result;
    }
    verifyCart(cart) {
      if (!this.pending) return;
      const { group, choices, quantity } = this.pending;
      const parent = cart.items.find(item => !isIcon(item) && item.properties?._hbr_icon_group === group);
      if (!parent || parent.quantity !== quantity) throw new Error('The item quantity could not be confirmed.');
      for (const choice of choices) {
        const child = cart.items.find(item => isIcon(item) && item.properties._hbr_icon_group === group && item.properties._hbr_icon_code === choice.code);
        if (!child || parentOf(child, cart.items) !== parent || child.quantity !== choice.count * quantity) {
          throw new Error('The item was added, but its paid icons could not be confirmed.');
        }
      }
      this.pending = null;
    }
    go(index, smooth = true) {
      index = Math.max(0, Math.min(this.options.length - 1, index));
      const option = this.options[index];
      const offset = option.offsetLeft + option.offsetWidth / 2 - this.wheel.clientWidth / 2;
      this.wheel.scrollTo({ left: offset, behavior: smooth && !this.reduced.matches ? 'smooth' : 'auto' });
      // Scroll position is authoritative while an animation is running.
      this.paint();
    }
    paint() {
      if (!this.wheel.clientWidth) return;
      const centre = this.wheel.scrollLeft + this.wheel.clientWidth / 2;
      const pitch = this.options.length > 1
        ? this.options[1].offsetLeft - this.options[0].offsetLeft
        : this.options[0].offsetWidth;
      let nearest = 0, distance = Infinity;
      this.options.forEach((option, index) => {
        const delta = (option.offsetLeft + option.offsetWidth / 2 - centre) / Math.max(1, pitch);
        if (Math.abs(delta) < distance) { nearest = index; distance = Math.abs(delta); }
        const face = option.firstElementChild;
        face.style.setProperty('--icon-angle', `${Math.max(-58, Math.min(58, -delta * 28))}deg`);
        face.style.setProperty('--icon-depth', `${-Math.min(90, Math.abs(delta) * 27)}px`);
        face.style.setProperty('--icon-scale', String(Math.max(.68, 1 - Math.abs(delta) * .12)));
        face.style.setProperty('--icon-opacity', String(Math.max(.35, 1 - Math.abs(delta) * .20)));
        option.tabIndex = index === nearest ? 0 : -1;
      });
      this.index = nearest;
      this.options.forEach((option, index) => { option.tabIndex = index === nearest ? 0 : -1; option.setAttribute('aria-pressed', String(index === nearest)); });
      this.querySelector('[data-icon-current]').textContent = this.options[nearest].dataset.iconName;
      this.querySelector('[data-icon-position]').textContent = `${nearest + 1} of ${this.options.length} · swipe to explore`;
      this.querySelector('[data-icon-prev]').disabled = nearest === 0;
      this.querySelector('[data-icon-next]').disabled = nearest === this.options.length - 1;
      this.add.setAttribute('aria-label', `Add ${this.options[nearest].dataset.iconName} for ${this.money(this.config.price)}`);
      this.querySelectorAll('[data-icon-grid-index]').forEach((button, index) => { button.setAttribute('aria-pressed', String(index === nearest)); });
    }
    renderSelected() {
      const list = this.querySelector('[data-icon-selected]');
      list.replaceChildren();
      for (const [code, count] of this.selected) {
        const option = this.options.find(option => option.dataset.iconCode === code);
        const li = document.createElement('li');
        const img = option.querySelector('img').cloneNode();
        img.alt = option.dataset.iconName;
        li.title = `${option.dataset.iconName} × ${count}`;
        const label = document.createElement('span');
        label.textContent = count > 1 ? `× ${count}` : '';
        label.hidden = count === 1;
        const remove = document.createElement('button');
        remove.type = 'button'; remove.textContent = '×'; remove.dataset.iconRemove = code;
        remove.disabled = !!this.busy;
        remove.setAttribute('aria-label', `Remove one ${option.dataset.iconName}`);
        li.append(img, label, remove); list.append(li);
      }
      list.hidden = !this.count;
      this.querySelector('[data-icon-total]').hidden = !this.count;
      this.querySelector('[data-icon-count]').textContent = `${this.count} ${this.count === 1 ? 'icon' : 'icons'} added`;
      this.querySelector('[data-icon-cost]').textContent = `+${this.money(this.count * this.config.price)}`;
      this.dispatchEvent(new CustomEvent('hbr:icons-change', { bubbles: true }));
    }
  }

  class IconCart extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.config = readJSON(this);
      this.controller = new AbortController();
      this.snapshot = this.config.cart;
      this.form._hbrIconLoading?.forEach(({ element, disabled, readOnly }) => {
        if (disabled !== undefined) element.disabled = disabled;
        if (readOnly !== undefined) element.readOnly = readOnly;
      });
      this.querySelector('[data-icon-cart-loading]')?.remove();
      const children = this.snapshot.items.filter(item => isIcon(item) && parentOf(item, this.snapshot.items));
      if (!children.length) return;
      children.forEach(item => {
        const row = this.form.querySelector(`[data-hbr-cart-line="${this.snapshot.items.indexOf(item) + 1}"]`);
        if (!row) return;
        const parent = parentOf(item, this.snapshot.items);
        row.classList.add('hbr-paid-icon');
        const quantity = row.querySelector('[name="updates[]"]');
        if (quantity) { quantity.readOnly = true; quantity.setAttribute('aria-label', 'Icon quantity, follows the item quantity'); }
        const note = document.createElement('p'); note.className = 'hbr-icon-cart-note';
        note.textContent = 'Changes with the item quantity. Remove to leave this icon off.';
        row.querySelector('.hbr-cart__details')?.append(note);
        const src = this.config.images[item.properties._hbr_icon_code];
        if (src) {
          let image = row.querySelector('.hbr-cart__image');
          if (image?.tagName !== 'IMG') {
            // The add-on has no product media: replace the placeholder itself.
            const replacement = document.createElement('img');
            replacement.className = 'hbr-cart__image';
            replacement.width = 100; replacement.height = 100;
            replacement.loading = 'lazy'; replacement.decoding = 'async';
            if (image) image.replaceWith(replacement);
            else row.querySelector('.hbr-cart__image-link')?.append(replacement);
            image = replacement;
          }
          image.removeAttribute('srcset'); image.removeAttribute('sizes');
          image.src = src; image.alt = item.properties.Icon || 'Icon';
        }
        row.querySelectorAll('.hbr-cart__image-link, .hbr-cart__product-title').forEach(link => { link.href = parent.url; });
      });
      this.form.addEventListener('submit', event => this.submit(event), { signal: this.controller.signal });
      this.form.addEventListener('click', event => {
        if (this.busy && event.target.closest('a')) event.preventDefault();
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      if (!this.form.reportValidity()) return;
      const inputs = [...this.form.querySelectorAll('[name="updates[]"]')];
      if (inputs.length !== this.snapshot.items.length) return this.fail('Your bag has changed.');
      const desired = inputs.map(input => integer(input.value, -1));
      if (desired.some(quantity => quantity < 0)) return this.fail('Please enter a valid quantity.');
      const checkout = event.submitter?.name === 'checkout';
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      const buttons = [...this.form.querySelectorAll('button')];
      buttons.forEach(button => { button.disabled = true; });
      inputs.forEach(input => { input.readOnly = true; });
      try {
        let cart = await cartRequest('cart.js');
        const signature = data => JSON.stringify(data.items.map(item => [item.key, item.quantity]));
        if (signature(cart) !== signature(this.snapshot)) throw new Error('Your bag was updated in another window.');
        // Change API validates inventory. Resolve fresh line positions after
        // each request because removals and discounts can change keys/order.
        for (let index = 0; index < this.snapshot.items.length; index++) {
          const original = this.snapshot.items[index];
          if (isIcon(original) && parentOf(original, this.snapshot.items)) continue;
          if (original.quantity === desired[index]) continue;
          const group = original.properties?._hbr_icon_group;
          const current = cart.items.find(item => group && !isIcon(original) ? !isIcon(item) && item.properties?._hbr_icon_group === group : item.key === original.key);
          if (!current) { if (desired[index] === 0) continue; throw new Error('Your bag has changed.'); }
          cart = await cartRequest('cart/change.js', { line: cart.items.indexOf(current) + 1, quantity: desired[index] });
          // Shopify may remove nested children with a parent; read authoritative cart.
          cart = await cartRequest('cart.js');
        }
        const children = this.snapshot.items.filter(item => isIcon(item) && parentOf(item, this.snapshot.items));
        for (const original of children) {
          const current = cart.items.find(item => isIcon(item) && item.properties._hbr_icon_group === original.properties._hbr_icon_group && item.properties._hbr_icon_code === original.properties._hbr_icon_code);
          if (!current) continue; // Removed with parent, or explicitly removed elsewhere.
          const parent = parentOf(current, cart.items);
          const units = integer(current.properties._hbr_icon_units);
          if (!parent || !units) throw new Error('An icon needs to be checked in your bag.');
          const quantity = units * parent.quantity;
          if (current.quantity !== quantity) {
            cart = await cartRequest('cart/change.js', { line: cart.items.indexOf(current) + 1, quantity });
            cart = await cartRequest('cart.js');
          }
        }
        // Only continue after Shopify confirms the paid quantities.
        for (const item of cart.items.filter(isIcon)) {
          const parent = parentOf(item, cart.items);
          if (parent && item.quantity !== integer(item.properties._hbr_icon_units) * parent.quantity) throw new Error('The icon quantities could not be updated.');
        }
        window.location.assign(checkout ? route('checkout') : this.form.action);
      } catch (error) {
        this.fail(error.message || 'Your bag could not be updated.');
      }
    }
    fail(message) {
      // A failed cart operation can still alter quantities. Require fresh cart
      // data instead of retrying against stale keys or proceeding to checkout.
      this.busy = true;
      this.form.querySelectorAll('button').forEach(button => { button.disabled = true; });
      const error = this.querySelector('[data-icon-cart-error]');
      error.hidden = false;
      error.querySelector('span').textContent = `${message} Refresh your bag to check it before continuing. `;
      error.focus({ preventScroll: false });
    }
  }
  customElements.define('hbr-icon-picker', IconPicker);
  customElements.define('hbr-icon-cart', IconCart);
})();
